/**
 * Utilitários para formatação de telefone
 */

/**
 * Formatar telefone para exibição
 * @param phone - Número de telefone sem formatação
 * @returns Telefone formatado (11) 99999-9999 ou (11) 9999-9999
 */
export const formatPhoneDisplay = (phone: string | null | undefined): string => {
  if (!phone) return 'Não informado';
  
  // Remove todos os caracteres não numéricos
  const cleanPhone = phone.replace(/\D/g, '');
  
  // Se não tem números suficientes, retorna como está
  if (cleanPhone.length < 10) return phone;
  
  // Formato para celular (11 dígitos): (11) 99999-9999
  if (cleanPhone.length === 11) {
    return cleanPhone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  
  // Formato para telefone fixo (10 dígitos): (11) 9999-9999
  if (cleanPhone.length === 10) {
    return cleanPhone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  
  // Se tem mais de 11 dígitos, pega apenas os primeiros 11
  if (cleanPhone.length > 11) {
    const truncated = cleanPhone.substring(0, 11);
    return truncated.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  
  return phone;
};

/**
 * Formatar telefone durante a digitação (máscara)
 * @param value - Valor atual do input
 * @returns Valor formatado com máscara
 */
export const formatPhoneInput = (value: string): string => {
  if (!value) return '';
  
  // Remove todos os caracteres não numéricos
  const cleanValue = value.replace(/\D/g, '');
  
  // Aplica a máscara conforme o número de dígitos
  if (cleanValue.length <= 2) {
    return cleanValue;
  } else if (cleanValue.length <= 3) {
    return `(${cleanValue.substring(0, 2)}) ${cleanValue.substring(2)}`;
  } else if (cleanValue.length <= 7) {
    return `(${cleanValue.substring(0, 2)}) ${cleanValue.substring(2, 7)}`;
  } else if (cleanValue.length <= 10) {
    return `(${cleanValue.substring(0, 2)}) ${cleanValue.substring(2, 6)}-${cleanValue.substring(6, 10)}`;
  } else {
    // Para celular (11 dígitos)
    return `(${cleanValue.substring(0, 2)}) ${cleanValue.substring(2, 7)}-${cleanValue.substring(7, 11)}`;
  }
};

/**
 * Remover formatação do telefone (para envio à API)
 * @param phone - Telefone formatado
 * @returns Telefone apenas com números
 */
export const cleanPhone = (phone: string): string => {
  return phone.replace(/\D/g, '');
};

/**
 * Validar formato de telefone brasileiro
 * @param phone - Telefone para validar
 * @returns true se válido, false caso contrário
 */
export const isValidPhone = (phone: string): boolean => {
  if (!phone) return true; // Campo opcional
  
  const cleanPhone = phone.replace(/\D/g, '');
  
  // Deve ter 10 ou 11 dígitos
  if (cleanPhone.length < 10 || cleanPhone.length > 11) return false;
  
  // Deve começar com código de área válido (11-99)
  const areaCode = parseInt(cleanPhone.substring(0, 2));
  if (areaCode < 11 || areaCode > 99) return false;
  
  // Para celular (11 dígitos), o terceiro dígito deve ser 9
  if (cleanPhone.length === 11 && cleanPhone[2] !== '9') return false;
  
  return true;
};